import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, Typography, Box } from '@mui/material';
import axios from 'axios';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import './DashboardCSMSelector.css';

const DashboardCSMSelector = ({ onCSMChange, selectedCSMId, csmInfo, activeClients, archivedClients }) => {
    const [availableCSMs, setAvailableCSMs] = useState([]);
    const [selectedCSM, setSelectedCSM] = useState('all');
    const [isLoading, setIsLoading] = useState(false);

    const workspaceId = Auth.getWorkspaceId();
    const user = Auth.getUser();
    const userId = user?.id;
    const isAdmin = user?.admin || false;

    useEffect(() => {
        if (selectedCSMId === null || selectedCSMId === undefined) {
            setSelectedCSM('all');
        } else if (selectedCSMId === 'all-csms') {
            setSelectedCSM('all-csms');
        } else {
            setSelectedCSM(selectedCSMId.toString());
        }
    }, [selectedCSMId]);

    useEffect(() => {
        const fetchAvailableCSMs = async () => {
            if (!workspaceId || !userId || !isAdmin) {
                return;
            }
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${Auth.getToken()}`,
                        },
                    }
                );
                setAvailableCSMs(response.data);
            } catch (err) {
                console.error("Failed to fetch available CSMs:", err);
                setAvailableCSMs([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAvailableCSMs();
    }, [workspaceId, userId, isAdmin]);

    const handleCSMChange = (event) => {
        const newSelectedCSM = event.target.value;
        setSelectedCSM(newSelectedCSM);

        let userIdToUse;
        if (newSelectedCSM === 'all') {
            userIdToUse = null; // All Clients
        } else if (newSelectedCSM === 'all-csms') {
            userIdToUse = 'all-csms'; // All CSMs
        } else {
            userIdToUse = newSelectedCSM; // Specific CSM
        }
        onCSMChange(userIdToUse);
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <Box className="dashboard-csm-selector-container">
            <Typography variant="h6" className="dashboard-csm-selector-title">
                View Dashboard for CSM
            </Typography>
            <FormControl className="dashboard-csm-selector-form-control">
                <Select
                    value={selectedCSM}
                    onChange={handleCSMChange}
                    disabled={isLoading}
                    className="dashboard-csm-selector-select"
                    displayEmpty
                    renderValue={(selected) => {
                        if (selected === 'all') {
                            return "All Clients";
                        }
                        if (selected === 'all-csms') {
                            return "All CSMs";
                        }

                        if (csmInfo && csmInfo.id.toString() === selected.toString()) {
                            return csmInfo.firstName && csmInfo.lastName 
                                ? `${csmInfo.firstName} ${csmInfo.lastName}`
                                : csmInfo.email;
                        }

                        const selectedCSMData = availableCSMs.find(csm => csm.id.toString() === selected.toString());
                        if (selectedCSMData) {
                            return selectedCSMData.firstName && selectedCSMData.lastName 
                                ? `${selectedCSMData.firstName} ${selectedCSMData.lastName}`
                                : selectedCSMData.email;
                        }
                        
                        return "Select CSM";
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 10 * 48,
                                width: 'auto',
                                maxWidth: '400px'
                            },
                        },
                    }}
                >
                    <MenuItem value="all" className="dashboard-csm-selector-menu-item">
                        <span className="dashboard-csm-selector-name">
                            All Clients
                        </span>
                    </MenuItem>
                    <MenuItem value="all-csms" className="dashboard-csm-selector-menu-item">
                        <span className="dashboard-csm-selector-name">
                            All CSMs
                        </span>
                    </MenuItem>
                    {availableCSMs.map((csm) => (
                        <MenuItem 
                            key={csm.id} 
                            value={csm.id}
                            className="dashboard-csm-selector-menu-item"
                        >
                            <span className="dashboard-csm-selector-name">
                                <span>
                                    {csm.firstName && csm.lastName 
                                        ? `${csm.firstName} ${csm.lastName}`
                                        : csm.email}
                                </span>
                                <span className="dashboard-csm-selector-client-count">
                                    ({csm.clientCount} clients)
                                </span>
                            </span>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            
            {/* Client Status Section */}
            <Box className="dashboard-csm-client-status">
                <Box className="client-status-item">
                    <Typography variant="body2" className="client-status-label">
                        Active Clients
                    </Typography>
                    <Typography variant="h6" className="client-status-count active">
                        {activeClients || 0}
                    </Typography>
                </Box>
                <Box className="client-status-item">
                    <Typography variant="body2" className="client-status-label">
                        Archived Clients
                    </Typography>
                    <Typography variant="h6" className="client-status-count archived">
                        {archivedClients || 0}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardCSMSelector;